import React, {
  ClipboardEvent,
  DragEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { applyAutoLinks } from './autoLink';
import { bodyToEditorHtml, serializeEditorContent } from './serialize';
import {
  StyledEditable,
  StyledEditorWrapper,
  StyledPlaceholder,
} from './styled';

export { MOD_KEY_LABEL } from './formatToolbar';

export interface RichTextEditorHandle {
  focus: () => void;
}

interface RichTextEditorProps {
  id?: string;
  value: string;
  placeholder: string;
  hasError?: boolean;
  fill?: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  focusRef?: React.MutableRefObject<HTMLElement | null>;
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    { id, value, placeholder, hasError, fill, onChange, onBlur, focusRef },
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const lastEmittedRef = useRef<string | null>(null);
    const [isEmpty, setIsEmpty] = useState(true);

    const emitChange = useCallback(
      (forceAll?: boolean) => {
        const editor = editorRef.current;
        if (!editor) return;
        applyAutoLinks(editor, forceAll === true);
        const html = serializeEditorContent(editor);
        const markup = editor.innerHTML;
        setIsEmpty(markup === '' || markup === '<br>');
        lastEmittedRef.current = html;
        onChange(html);
      },
      [onChange],
    );

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || value === lastEmittedRef.current) return;
      editor.innerHTML = bodyToEditorHtml(value);
      applyAutoLinks(editor, true);
      const html = serializeEditorContent(editor);
      lastEmittedRef.current = html;
      setIsEmpty(!html.trim());
      if (html !== value) onChange(html);
    }, [value, onChange]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      },
    }));

    const insertPlainText = (text: string, linkifyAll = false) => {
      if (!text) return;
      document.execCommand('insertText', false, text);
      emitChange(linkifyAll);
    };

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      insertPlainText(event.clipboardData.getData('text/plain'), true);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
      if (event.dataTransfer.types.includes('Files')) return;
      event.preventDefault();
      const doc = document as Document & {
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
      };
      const range = doc.caretRangeFromPoint?.(event.clientX, event.clientY);
      if (range) {
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      insertPlainText(event.dataTransfer.getData('text/plain'), true);
    };

    return (
      <StyledEditorWrapper ref={wrapperRef} $fill={fill}>
        {isEmpty && <StyledPlaceholder>{placeholder}</StyledPlaceholder>}
        <StyledEditable
          id={id}
          ref={(node) => {
            editorRef.current = node;
            if (focusRef) focusRef.current = node;
          }}
          $fill={fill}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          aria-invalid={hasError}
          spellCheck
          onInput={() => emitChange()}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onBlur={() => {
            emitChange(true);
            onBlur?.();
          }}
        />
      </StyledEditorWrapper>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
